/**
 * Mobile Context Types
 *
 * Enhanced mobile detection and context management for the layout system redesign.
 * These types extend the existing useMobileDetection functionality with comprehensive
 * device classification, viewport tracking, and performance optimizations.
 */

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

/**
 * Enhanced mobile context interface
 * This extends the basic isMobile detection with comprehensive device information
 */
export interface MobileContextType {
  // Basic mobile detection (backwards compatible with existing useMobileDetection)
  isMobile: boolean;

  // Enhanced device classification
  deviceType: DeviceType;

  // Viewport information with debounced updates
  viewportWidth: number;
  viewportHeight: number;
  orientation: Orientation;

  // Performance-aware features
  isLowMemoryDevice: boolean;
  preferReducedMotion: boolean;

  // Utility functions
  isPhone: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Breakpoint utilities
  isExtraSmall: boolean; // < 480px
  isSmall: boolean; // 480px - 768px
  isMedium: boolean; // 768px - 1024px
  isLarge: boolean; // 1024px - 1440px
  isExtraLarge: boolean; // > 1440px
}

/**
 * Mobile context provider props
 */
export interface MobileContextProviderProps {
  children: React.ReactNode;

  // Configuration options
  debounceMs?: number; // Default: 300ms
  enablePerformanceMonitoring?: boolean; // Default: true

  // Custom breakpoints (optional override)
  breakpoints?: {
    mobile: number; // Default: 768px
    tablet: number; // Default: 1024px
    desktop: number; // Default: 1440px
  };
}

/**
 * Device classification thresholds
 */
export const DEFAULT_BREAKPOINTS = {
  extraSmall: 480, // Phone portrait
  small: 768, // Phone landscape / small tablet
  medium: 1024, // Tablet / small laptop
  large: 1440, // Desktop / large laptop
  extraLarge: 1920, // Large desktop
} as const;

/**
 * Performance monitoring configuration
 */
export interface PerformanceConfig {
  enableMemoryMonitoring: boolean;
  enableFPSMonitoring: boolean;
  enableLayoutTiming: boolean;
  reportingInterval: number; // ms
}

/**
 * Mobile context hook options
 */
export interface UseMobileContextOptions {
  // Subscribe only to specific context changes (performance optimization)
  subscriptions?: Array<keyof MobileContextType>;

  // Enable debugging in development
  debug?: boolean;
}
